// User profile as described in the BRD data model
export interface UserProfile {
  id: string;
  yearly_target_hours: number;
  tracking_start_date: string; // ISO date format
  tracking_end_date: string; // ISO date format
  work_week_days: number[]; // 0-6 for Sun-Sat
  setup_completed: boolean; // Flag to determine if setup wizard was completed
  created_at?: string;
  updated_at?: string;
}

// Vacation day as described in the BRD data model
export interface VacationDay {
  id: string;
  profile_id: string;
  vacation_date: string; // ISO date format
  created_at?: string;
}

// Daily entry as described in the BRD data model
export interface DailyEntry {
  id: string;
  profile_id: string;
  entry_date: string; // ISO date format
  billed_hours: number;
  created_at?: string;
  updated_at?: string;
}

// Daily target for storing pre-calculated target hours
export interface DailyTarget {
  profile_id: string;
  target_date: string;           // ISO date format
  avg_daily_target: number;      // Average hours needed across all days
  required_daily_target: number; // Dynamically calculated target for this specific day
  remaining_hours: number;       // Hours remaining to hit yearly target as of this date
  remaining_days: number;        // Working days remaining as of this date
  is_work_day: boolean;          // Whether this is a work day
  is_vacation_day: boolean;      // Whether this is a vacation day
  calculated_at: string;         // When this target was calculated
}

// Type for tracking calculation results
export interface CalculationResults {
  totalWorkDays: number;
  netAvailableWorkDays: number;
  remainingWorkDays: number;
  requiredAverageDailyHours: number;
  requiredDailyHoursForToday: number;
  totalHoursBilled: number;
  remainingHoursNeeded: number;
  progressPercentage: number;
}

// Target summary for storing pre-calculated summary values
export interface TargetSummary {
  profile_id: string;
  yearly_target_hours: number;
  avg_daily_target: number;      // Average hours needed across all days
  current_daily_target: number;  // Current daily target for today and future days
  total_billed_hours: number;    // Total hours billed to date
  remaining_hours: number;       // Hours remaining to hit yearly target
  total_work_days: number;       // Total work days in tracking period
  remaining_work_days: number;   // Work days remaining from today
  calculated_at: string;         // When this summary was calculated
}

// Type for daily breakdown data within a month
export interface DayEntry {
  date: string; // Format: "YYYY-MM-DD"
  displayDate: string; // Format: "Mon, Apr 1"
  dayOfWeek: number; // 0-6 for Sun-Sat
  hasEntry: boolean;
  entry: DailyEntry | null;
}

// Type for monthly breakdown data
export interface MonthlyBreakdown {
  month: string; // Format: "YYYY-MM"
  displayName: string; // Format: "October 2023"
  entries: DailyEntry[];
  days?: DayEntry[]; // New field for day-level breakdown
  totalHours: number;
}

// Auth state type
export interface AuthState {
  user: any | null;
  session: any | null;
  loading: boolean;
}

// Auth signup response type
export interface SignUpResponse {
  user: any | null;
  session: any | null;
  emailConfirmationRequired: boolean;
}

// Setup wizard steps
export enum SetupStep {
  TARGET_HOURS = 0,
  DATE_RANGE = 1,
  WORK_WEEK = 2,
  VACATION_DAYS = 3,
  COMPLETE = 4
}